import { Button, Drawer, Flex, Layout, Menu, Modal, Space, Tooltip } from "antd";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MenuProps } from "antd";
import { UserInfo } from "../../components/molecules";
import { BranchSwitcher } from "../../components/molecules/branch-switcher";
import styled from "styled-components";
import { Calendar, CallBell, GearSix, Megaphone, List, X, ChartBar, GlobeHemisphereEast, Question, SignOut } from "@phosphor-icons/react";
import { useResponsive } from "../../hooks/useResponsive";
import { useUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import { colors } from "../../styles/theme";
import Cookies from "js-cookie";
import { useQuery } from "react-query";
import { getCurrentUser, getCompany } from "../../service/client";
import { useBranchContext } from "../../contexts/branch-context/useBranchContext";
import { useTourContext } from "../../contexts/tour-context/useTourContext";
import { useAdditionsTour } from "../../contexts/additions-tour-context/useAdditionsTour";

const CostumMenu = styled(Menu)`
  &.ant-menu {
    border-right: none !important;
    padding: 0;
  }

  & .ant-menu-item {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 48px !important;
    height: 48px !important;
    margin: 0 auto 8px auto !important;
    line-height: 48px !important;

    &:last-child {
      margin-bottom: 0 !important;
    }

    .ant-menu-title-content {
      display: none !important;
    }

    .ant-menu-item-icon {
      margin: 0 !important;
      margin-inline-end: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 24px !important;
      height: 24px !important;
      color: var(--color-text) !important;

      svg {
        display: block !important;
        width: 24px !important;
        height: 24px !important;
        color: inherit !important;
        fill: currentColor !important;
        stroke: currentColor !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
    }

    &.ant-menu-item-selected {
      background-color: ${colors.primaryLight} !important;
      border-radius: 8px;

      .ant-menu-item-icon {
        color: ${colors.primary} !important;

        svg {
          color: ${colors.primary} !important;
        }
      }
    }

    &:hover {
      background-color: ${colors.backgroundHover} !important;
      border-radius: 8px;
    }

  }
`;

// Styled component dla mobile topbar
const MobileTopbar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
`;

const MenuButton = styled(Button)`
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-radius: 4px;
  }
`;

// Styled menu dla mobile drawer (pionowe z etykietami)
const MobileMenu = styled(Menu)`
  overflow-x: hidden !important;

  &.ant-menu {
    border-right: none !important;
  }

  & .ant-menu-item {
    padding: 24px 24px !important;
    margin: 0 !important;
    height: auto !important;
    border-radius: 0 !important;
    color: var(--color-text) !important;

    .ant-menu-item-icon {
      color: var(--color-text) !important;
      svg {
        color: var(--color-text) !important;
      }
    }

    .ant-menu-title-content {
      color: var(--color-text) !important;
      font-size: 14px !important;
      line-height: 22px !important;
    }

    &.ant-menu-item-selected {
      background-color: ${colors.primaryLight} !important;
      color: ${colors.primary} !important;

      .ant-menu-item-icon {
        color: ${colors.primary} !important;
        svg { color: ${colors.primary} !important; }
      }

      .ant-menu-title-content {
        color: ${colors.primary} !important;
      }
    }

    &:hover {
      background-color: ${colors.backgroundHover} !important;
      color: var(--color-text) !important;
    }
  }
`;

// ── Tutorial card for the Samouczki modal ─────────────────────────────────
const TutorialCard: React.FC<{ emoji: string; title: string; desc: string; onStart: () => void }> = ({ emoji, title, desc, onStart }) => {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "16px", borderRadius: 8, border: "1px solid var(--color-divider)", background: "var(--color-surface)" }}>
      <span style={{ fontSize: 26, lineHeight: 1, minWidth: 32, textAlign: "center" }}>{emoji}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
        <div style={{ color: "var(--color-text-secondary)", fontSize: 13, marginBottom: 12 }}>{desc}</div>
        <Button size="small" type="primary" onClick={onStart}>{t("nav.start")}</Button>
      </div>
    </div>
  );
};

export const SlideMenu: React.FC<{
  refetch: any;
  collapsed: any;
  setCollapsed: any;
}> = ({ refetch, collapsed, setCollapsed }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const { requestNavigation } = useUnsavedChanges();

  const { data: currentUser } = useQuery(
    ["/admin/current_user"],
    () => getCurrentUser()
  );

  const { currentBranch } = useBranchContext();
  const { data: company } = useQuery(["/company"], getCompany);
  const { ordersNavRef, mobileOrdersMenuRef, mobileMenuBtnRef, globeRef, isTourOpen, tourStep, stepIndexes, advanceTourStep, startTour } = useTourContext();
  const { startTour: startAdditionsTour, isTourOpen: additionsTourOpen, tourStep: additionsTourStep, advanceStep: advanceAdditionsStep } = useAdditionsTour();

  const menuUrl =
    company?.slug && currentBranch
      ? `https://proste.menu/${company.slug}/${currentBranch.slug ?? currentBranch.id}`
      : null;

  type MenuItem = Required<MenuProps>["items"][number];

  function getItem(
    label: React.ReactNode,
    key: React.Key,
    path: string,
    icon?: React.ReactNode,
    children?: MenuItem[],
    onClickExtra?: () => void
  ): MenuItem {
    return {
      key,
      icon,
      children,
      label,
      onClick: () => {
        if (onClickExtra) onClickExtra();

        if (path) {
          const canNavigate = requestNavigation(() => {
            navigate(path);
            if (isMobile) setDrawerOpen(false);
          });

          if (canNavigate) {
            navigate(path);
            if (isMobile) setDrawerOpen(false);
          }
        }
      },
    } as MenuItem;
  }

  const regularItems: MenuItem[] = [
    getItem(
      t("nav.orders"),
      "orders",
      "orders",
      <CallBell size={24} weight="regular" />,
      undefined,
      () => {
        // Original Menu produktów tour
        if (isTourOpen && tourStep === stepIndexes.ORDERS_NAV) advanceTourStep(stepIndexes.ORDERS_NAV);
        // Additions tours – ORDERS_NAV: Desktop=1, Mobile=2
        const ordersNavStep = isMobile ? 2 : 1;
        if (additionsTourOpen && additionsTourStep === ordersNavStep) {
          setTimeout(() => advanceAdditionsStep(ordersNavStep), 400);
        }
      }
    ),
    getItem(
      t("nav.reservations"),
      "reservations",
      "reservations",
      <Calendar size={24} weight="regular" />
    ),
    getItem(
      t("nav.marketing"),
      "marketing",
      "marketing",
      <Megaphone size={24} weight="regular" />
    ),
    getItem(
      t("nav.stats"),
      "stats",
      "stats",
      <ChartBar size={24} weight="regular" />
    ),
  ];

  const isWorker = currentUser?.role === "WORKER";
  const companyBlocked = company?.isActive === false;

  const filteredItems = companyBlocked
    ? [getItem(t("nav.settings"), "settings", "settings", <GearSix size={24} weight="regular" />)]
    : regularItems.filter((item) => {
      if (!isWorker) return true;
      return ["orders", "reservations"].includes(String(item?.key));
    });

  const isOrdersTourStep = isTourOpen && tourStep === stepIndexes.ORDERS_NAV;
  const additionsOrdersNavStep = isMobile ? 2 : 1;
  const isAdditionsOrdersTourStep = additionsTourOpen && additionsTourStep === additionsOrdersNavStep;

  const items: MenuItem[] = (isOrdersTourStep || isAdditionsOrdersTourStep)
    ? filteredItems.map((item) =>
      item && (item as { key?: React.Key }).key !== "orders"
        ? { ...item, disabled: true }
        : item
    )
    : filteredItems;

  const getSelectedKey = (): string[] => {
    const path = location.pathname;
    if (path.startsWith("/orders") || path.startsWith("/check-order")) return ["orders"];
    if (path.startsWith("/reservations")) return ["reservations"];
    if (path.startsWith("/menu") || path.startsWith("/edit-product")) return ["menu"];
    if (path.startsWith("/services") || path.startsWith("/resources")) return ["services"];
    if (path.startsWith("/marketing")) return ["marketing"];
    if (path.startsWith("/coupons")) return ["coupons"];
    if (path.startsWith("/stats")) return ["stats"];
    return [];
  };

  // Wspólny styl wiersza w dolnej sekcji mobilnego drawera
  const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 24px",
    width: "100%",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "var(--color-text)",
    fontSize: 14,
    lineHeight: "22px",
    textDecoration: "none",
  };

  const handleLogout = async () => {
    Cookies.remove("accessToken");
    Cookies.remove("role");
    await refetch();
    navigate("/");
  };

  // Renderuj mobile topbar dla urządzeń mobilnych
  if (isMobile) {
    return (
      <>
        <MobileTopbar>
          <div style={{ width: "40px" }}>
            <BranchSwitcher collapsed={true} />
          </div>
          <div ref={mobileMenuBtnRef} style={{ display: "inline-flex" }}>
            <MenuButton
              onClick={() => {
                setDrawerOpen(true);
                // Original tour (Menu produktów)
                if (isTourOpen && tourStep === stepIndexes.HAMBURGER_1) {
                  setTimeout(() => advanceTourStep(stepIndexes.HAMBURGER_1), 400);
                }
                if (isTourOpen && tourStep === stepIndexes.HAMBURGER_2) {
                  setTimeout(() => advanceTourStep(stepIndexes.HAMBURGER_2), 400);
                }
                // Additions tours – mobile step 1 is always the hamburger in both tours
                if (additionsTourOpen && additionsTourStep === 1) {
                  setTimeout(() => advanceAdditionsStep(1), 400);
                }
              }}
            >
              <List size={28} weight="regular" />
            </MenuButton>
          </div>
        </MobileTopbar>

        <Drawer
          title={
            <Flex justify="space-between" align="center">
              <span>{t("nav.menu")}</span>
              <MenuButton onClick={() => setDrawerOpen(false)}>
                <X size={24} weight="bold" />
              </MenuButton>
            </Flex>
          }
          placement="right"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={280}
          forceRender
          styles={{
            header: { paddingBottom: 16, borderBottom: "1px solid var(--color-divider)" },
            body: { padding: 0, overflowX: "hidden" },
          }}
          closeIcon={null}
        >
          <Flex vertical justify="space-between" style={{ height: "100%" }}>
            {/* GÓRA: pozycje nawigacyjne */}
            <div ref={mobileOrdersMenuRef}>
              <MobileMenu
                theme="light"
                mode="inline"
                items={items}
                selectedKeys={getSelectedKey()}
                style={{ border: "none" }}
              />
            </div>

            {/* DÓŁ: akcje pomocnicze */}
            <div style={{ borderTop: "1px solid var(--color-divider)" }}>
              <button style={rowStyle} onClick={() => { setDrawerOpen(false); setHelpModalOpen(true); }}>
                <Question size={24} weight="regular" />
                <span>{t("nav.tutorials")}</span>
              </button>

              {menuUrl && (
                <a
                  ref={globeRef}
                  href={menuUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={rowStyle}
                  onClick={() => { if (isTourOpen && tourStep === stepIndexes.GLOBE) advanceTourStep(stepIndexes.GLOBE); }}
                >
                  <GlobeHemisphereEast size={24} weight="regular" />
                  <span>{t("nav.openClientMenu")}</span>
                </a>
              )}

              <button style={rowStyle} onClick={() => { setDrawerOpen(false); navigate("/settings"); }}>
                <GearSix size={24} weight="regular" />
                <span>{t("nav.settings")}</span>
              </button>

              <button style={{ ...rowStyle, color: "var(--color-error, #ff4d4f)" }} onClick={handleLogout}>
                <SignOut size={24} weight="regular" />
                <span>{t("nav.logout")}</span>
              </button>
            </div>
          </Flex>
        </Drawer>

        <Modal
          open={helpModalOpen}
          onCancel={() => setHelpModalOpen(false)}
          footer={null}
          title={t("nav.tutorials")}
          width={460}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
            <TutorialCard emoji="🍽️" title={t("nav.tutorialMenuTitle")} desc={t("nav.tutorialMenuDesc")} onStart={() => { setHelpModalOpen(false); startTour(); }} />
            <TutorialCard emoji="➕" title={t("nav.tutorialAdditionsTitle")} desc={t("nav.tutorialAdditionsDesc")} onStart={() => { setHelpModalOpen(false); startAdditionsTour("create-additions"); }} />
            <TutorialCard emoji="🔗" title={t("nav.tutorialAttachTitle")} desc={t("nav.tutorialAttachDesc")} onStart={() => { setHelpModalOpen(false); navigate("/menu"); startAdditionsTour("attach-additions"); }} />
          </div>
        </Modal>
      </>
    );
  }

  // Renderuj desktop sidebar dla większych ekranów
  return (
    <>
      <Layout.Sider
        collapsed={true}
        style={{
          overflow: "auto",
          height: "100vh",
          position: "fixed",
          background: "var(--color-surface)",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 1000,
          borderRight: "1px solid var(--color-divider)",
        }}
      >
        <Flex justify="space-between" vertical style={{ height: "100%" }}>
          <Space direction="vertical" style={{ width: "100%", padding: "16px" }}>
            <Flex justify="center" style={{ marginBottom: "8px" }}>
              <BranchSwitcher collapsed={true} />
            </Flex>
            <div ref={ordersNavRef}>
              <CostumMenu
                theme="light"
                mode="inline"
                items={items}
                selectedKeys={getSelectedKey()}
              />
            </div>
          </Space>
          <div>
            <Flex justify="center" style={{ padding: "8px 8px 0" }}>
              <Tooltip title={t("nav.tutorials")} placement="right">
                <button
                  onClick={() => setHelpModalOpen(true)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 48,
                    height: 48,
                    borderRadius: 8,
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--color-text)",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = colors.backgroundHover)}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <Question size={24} weight="regular" />
                </button>
              </Tooltip>
            </Flex>
            {menuUrl && (
              <Flex justify="center" style={{ padding: "8px 8px 0" }}>
                <Tooltip title={t("nav.openClientMenu")} placement="right">
                  <a
                    ref={globeRef}
                    href={menuUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      color: "var(--color-text)",
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = colors.backgroundHover)}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}
                    onClick={() => { if (isTourOpen && tourStep === stepIndexes.GLOBE) advanceTourStep(stepIndexes.GLOBE); }}
                  >
                    <GlobeHemisphereEast size={24} weight="regular" />
                  </a>
                </Tooltip>
              </Flex>
            )}
            <UserInfo refetch={refetch} collapsed={collapsed} />
          </div>
        </Flex>
      </Layout.Sider>

      <Modal
        open={helpModalOpen}
        onCancel={() => setHelpModalOpen(false)}
        footer={null}
        title={t("nav.tutorials")}
        width={460}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
          <TutorialCard emoji="🍽️" title={t("nav.tutorialMenuTitle")} desc={t("nav.tutorialMenuDesc")} onStart={() => { setHelpModalOpen(false); startTour(); }} />
          <TutorialCard emoji="➕" title={t("nav.tutorialAdditionsTitle")} desc={t("nav.tutorialAdditionsDesc")} onStart={() => { setHelpModalOpen(false); startAdditionsTour("create-additions"); }} />
          <TutorialCard emoji="🔗" title={t("nav.tutorialAttachTitle")} desc={t("nav.tutorialAttachDesc")} onStart={() => { setHelpModalOpen(false); navigate("/menu"); startAdditionsTour("attach-additions"); }} />
        </div>
      </Modal>
    </>
  );
};
